import { useCallback, useMemo, useState } from 'react'
import type React from 'react'
import type { ReserveOptionMenuInput } from '~/lib/graphql/graphql'
import type { OptionMenuId } from '~/lib/graphql/scalars'
import { useVisitorsCount } from '../store/stateMachine/context'
import type { ReserveInputOptionMenu } from '../store/stateMachine/state'

export function useMenuCard(
  option: ReserveInputOptionMenu,
  selectedOptions: ReserveOptionMenuInput[],
  onSelect: (e: React.ChangeEvent<HTMLSelectElement>, option: ReserveInputOptionMenu) => void
) {
  const [opened, setOpened] = useState(false)
  const toggle = useCallback(() => {
    setOpened((prev) => !prev)
  }, [setOpened])

  const visitorsCount = useVisitorsCount()
  const availableInventory = getAvailableInventory(visitorsCount, option)
  const values = useMemo(
    () => Array.from({ length: availableInventory + 1 }, (_, i) => `${i}点`),
    [availableInventory]
  )

  const selectedCount = getSelectedCount(selectedOptions, option)
  const value = `${selectedCount}点`

  const onChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => onSelect(e, option),
    [onSelect, option]
  )

  const props = useMemo(
    () => ({
      values,
      value,
      label: value,
      onChange,
    }),
    [values, value, onChange]
  )

  return { opened, toggle, props }
}

function getAvailableInventory(visitorsCount: number, option: ReserveInputOptionMenu) {
  return Math.min(
    visitorsCount,
    ...(option.availability ? [option.availability] : []),
    ...(option.optionMenu.maxOrderPerReservation ? [option.optionMenu.maxOrderPerReservation] : [])
  )
}
if (import.meta.vitest) {
  const { it, expect } = import.meta.vitest
  function createOption(availability: number | null, maxOrderPerReservation: number | null) {
    return {
      availability,
      optionMenu: { maxOrderPerReservation: maxOrderPerReservation },
    } as ReserveInputOptionMenu
  }
  it('getAvailableInventory', () => {
    expect(getAvailableInventory(2, createOption(null, null))).toBe(2)
    expect(getAvailableInventory(2, createOption(5, null))).toBe(2)
    expect(getAvailableInventory(2, createOption(null, 5))).toBe(2)
    expect(getAvailableInventory(2, createOption(5, 5))).toBe(2)
    expect(getAvailableInventory(3, createOption(2, 1))).toBe(1)
  })
}

function getSelectedCount(
  selectedOptions: ReserveOptionMenuInput[],
  option: ReserveInputOptionMenu
) {
  const selectedOption = selectedOptions.find(
    (selected) => selected.optionMenuId === option.optionMenu.optionMenuId
  )
  return selectedOption?.count ?? 0
}
if (import.meta.vitest) {
  const { it, expect } = import.meta.vitest
  function createOption(id: OptionMenuId) {
    return { optionMenu: { optionMenuId: id } } as ReserveInputOptionMenu
  }
  const selectedOptions = [
    { optionMenuId: '1', count: 1 },
    { optionMenuId: '2', count: 2 },
  ] as ReserveOptionMenuInput[]
  it('getSelectedCount', () => {
    expect(getSelectedCount(selectedOptions, createOption('1' as OptionMenuId))).toBe(1)
    expect(getSelectedCount(selectedOptions, createOption('3' as OptionMenuId))).toBe(0)
  })
}
